import type { ReactElement } from 'react';
import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import { Box } from 'components/ui';
import DashboardComponentCard from './DashboardComponentCard';
import { dashboardGridSx, dashboardTileSx, DEFAULT_GRID_COLUMNS } from './DashboardGrid.styles';
import type { DashboardDataView } from '../../dashboardData.types';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../SummaryDashboard.constants';

interface DashboardGridProps {
  definition: DashboardDefinition;
  data?: DashboardDataView;
  // Opens the Edit Summary dialog on a tile's own tab. Undefined for a definition the user cannot
  // edit, which is what keeps the per-tile buttons off those dashboards entirely.
  onEditComponent?: (componentId?: string) => void;
}

// Renders the AI's layout: one card per component, placed on a static CSS grid from the schema's
// position/span. Resolved values arrive as one already-fetched map from SummaryDashboard and are
// looked up here by component id; component types with no data contract yet keep their placeholder.
const DashboardGrid = ({ definition, data, onEditComponent }: DashboardGridProps): ReactElement => {
  const columns = definition.layout?.columns ?? DEFAULT_GRID_COLUMNS;

  return (
    <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.grid} sx={dashboardGridSx(columns)}>
      {definition.components.map((component, index) => (
        <Box
          // Ids are server-assigned and unique, but fall back to the index so a hand-written or
          // legacy definition without ids still renders instead of collapsing to one keyed child.
          key={component.id ?? `${component.type}-${index}`}
          sx={dashboardTileSx(component.position)}>
          <DashboardComponentCard
            component={component}
            componentData={component.id ? data?.byComponentId.get(component.id) : undefined}
            currencyCode={data?.currency}
            isDataError={data?.isError}
            isDataLoading={data?.isLoading}
            // A legacy component with no id still gets a button; the dialog falls back to its
            // Dashboard tab rather than selecting a tab whose id it cannot match.
            onEdit={onEditComponent ? () => onEditComponent(component.id) : undefined}
          />
        </Box>
      ))}
    </Box>
  );
};

export default DashboardGrid;
